/**
 * LeaderboardPage — Steward Admin
 * Real-time leaderboard preview matching what customers see.
 * Includes per-round advancement controls — select an event/round, then
 * mark which drivers advance from that round to the next.
 * Stores `advancedFrom: [eventId, ...]` array on each driver document.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  onDrivers, onEvents, onLeaderboard,
  advanceDriver, unadvanceDriver,
  recomputeLeaderboard, TITLES,
} from '../services/tripleCrown';
import useChampionship from '../hooks/useChampionship';

export default function LeaderboardPage() {
  const { champ } = useChampionship();
  const [drivers, setDrivers] = useState([]);
  const [events, setEvents] = useState([]);
  const [board, setBoard] = useState(null);
  const [activeTab, setActiveTab] = useState('overall');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [recomputing, setRecomputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Build maps
  const driverMap = useMemo(() => {
    const m = {};
    drivers.forEach(d => { m[d.id] = d; });
    return m;
  }, [drivers]);

  const eventMap = useMemo(() => {
    const m = {};
    events.forEach(e => { m[e.id] = e; });
    return m;
  }, [events]);

  // Sorted events for the dropdown
  const sortedEvents = useMemo(() =>
    [...events].sort((a, b) => {
      const aD = a.scheduledDate?.toMillis ? a.scheduledDate.toMillis() : 0;
      const bD = b.scheduledDate?.toMillis ? b.scheduledDate.toMillis() : 0;
      return aD - bD;
    }), [events]);

  useEffect(() => {
    if (!champ) { setDrivers([]); setEvents([]); return; }
    const unsubs = [
      onDrivers(champ.id, setDrivers),
      onEvents(champ.id, setEvents),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [champ]);

  useEffect(() => {
    const id = activeTab === 'overall' ? 'leaderboard_overall' : `leaderboard_${activeTab}`;
    return onLeaderboard(id, setBoard);
  }, [activeTab]);

  const isTimeBased = board?.sortedBy === 'time';
  const standings = useMemo(() => {
    if (!board?.standings) return [];
    if (board.sortedBy === 'time') return board.standings;
    return [...board.standings].sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0));
  }, [board]);

  const handleRecompute = async () => {
    if (!champ) return;
    setRecomputing(true);
    try {
      await recomputeLeaderboard({ championshipId: champ.id });
      setToast({ type: 'success', msg: 'Leaderboard recomputed' });
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Recompute failed' });
    } finally { setRecomputing(false); }
  };

  const handleToggleAdvanced = async (driverId, isCurrentlyAdvanced) => {
    if (!selectedEventId) {
      setToast({ type: 'error', msg: 'Select a round first to manage advancement' });
      return;
    }
    setSaving(true);
    try {
      if (isCurrentlyAdvanced) {
        await unadvanceDriver(driverId, selectedEventId);
        setToast({ type: 'success', msg: 'Advancement removed' });
      } else {
        await advanceDriver(driverId, selectedEventId);
        setToast({ type: 'success', msg: 'Driver marked as advancing' });
      }
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to update' });
    } finally { setSaving(false); }
  };

  // Check if a driver is advanced from the currently selected event
  const isAdvancedFromSelected = (driverId) => {
    if (!selectedEventId) return false;
    const d = driverMap[driverId];
    return (d?.advancedFrom || []).includes(selectedEventId);
  };

  // Check if a driver is advanced from ANY event (for display when no event is selected)
  const isAdvancedFromAny = (driverId) => {
    const d = driverMap[driverId];
    return (d?.advancedFrom || []).length > 0;
  };

  // Get event names a driver has advanced from
  const getAdvancedRounds = (driverId) => {
    const d = driverMap[driverId];
    if (!d?.advancedFrom?.length) return [];
    return d.advancedFrom.map(eid => {
      const ev = eventMap[eid];
      return ev ? (ev.phaseName || `Phase ${ev.phase}`) : eid;
    });
  };

  // Count advanced for selected event
  const advancedCount = useMemo(() => {
    if (!selectedEventId) return standings.filter(e => isAdvancedFromAny(e.driverId)).length;
    return standings.filter(e => isAdvancedFromSelected(e.driverId)).length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standings, driverMap, selectedEventId]);

  const selectedEvent = selectedEventId ? eventMap[selectedEventId] : null;

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 3000); return () => clearTimeout(t); }
  }, [toast]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Leaderboard Preview</h1>
          <p className="page-subtitle">Real-time standings as customers see them</p>
        </div>
        <button className="btn btn-secondary" onClick={handleRecompute} disabled={recomputing}>
          {recomputing ? 'Recomputing...' : 'Force Recompute'}
        </button>
      </div>

      {/* Title Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className={`btn btn-sm ${activeTab === 'overall' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('overall')}>Overall</button>
        {TITLES.map(t => (
          <button key={t.id} className={`btn btn-sm ${activeTab === t.id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab(t.id)}>{t.short}</button>
        ))}
      </div>

      {/* Advancement Controls */}
      <div className="card" style={{ marginBottom: 18, padding: '14px 18px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-soft)', whiteSpace: 'nowrap' }}>Advance from round:</label>
          <select
            className="form-input"
            value={selectedEventId}
            onChange={e => setSelectedEventId(e.target.value)}
            style={{ maxWidth: 340, fontSize: 13 }}
          >
            <option value="">— Select a round —</option>
            {sortedEvents.map(ev => (
              <option key={ev.id} value={ev.id}>
                {ev.phaseName || `Phase ${ev.phase}`} — {ev.track || 'No track'}
              </option>
            ))}
          </select>
          {advancedCount > 0 && (
            <span style={{ fontSize: 13, color: '#22c55e', fontWeight: 600 }}>
              ✓ {advancedCount} driver{advancedCount !== 1 ? 's' : ''} advancing
            </span>
          )}
        </div>
        {!selectedEventId && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>
            Select a round above to mark which drivers advance to the next stage. Advancement is tracked per round.
          </p>
        )}
      </div>

      {standings.length === 0 ? (
        <div className="empty-state">
          <h3>No standings data</h3>
          <p>Submit results to populate the leaderboard</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>Pos</th>
                  <th></th>
                  <th>Driver</th>
                  <th>Team</th>
                  {isTimeBased ? <th>Best Time</th> : <th>Points</th>}
                  <th>Wins</th>
                  <th>Podiums</th>
                  <th>Races</th>
                  <th style={{ width: 120, textAlign: 'center' }}>Advance</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((entry, idx) => {
                  const rank = idx + 1;
                  const driverDoc = driverMap[entry.driverId] || {};
                  const advancedThisRound = isAdvancedFromSelected(entry.driverId);
                  const advancedAny = isAdvancedFromAny(entry.driverId);
                  const advancedRounds = getAdvancedRounds(entry.driverId);
                  const posColor = rank === 1 ? 'var(--gold)' : rank === 2 ? '#c0c0c0' : rank === 3 ? '#cd7f32' : 'var(--text)';
                  const rowBg = advancedThisRound
                    ? 'rgba(34, 197, 94, 0.08)'
                    : (selectedEventId && advancedAny ? 'rgba(34, 197, 94, 0.03)' : undefined);
                  return (
                    <tr key={entry.driverId} style={rowBg ? { background: rowBg } : undefined}>
                      <td style={{ fontWeight: 700, color: posColor, fontSize: 16 }}>{rank}</td>
                      <td style={{ width: 40 }}>
                        {entry.photoUrl ? (
                          <img src={entry.photoUrl} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                            {driverDoc.name?.[0] || entry.driverName?.[0] || '?'}
                          </div>
                        )}
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        <div>{entry.driverName}</div>
                        {advancedRounds.length > 0 && (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
                            {advancedRounds.map((rnd, i) => (
                              <span key={i} style={{
                                fontSize: 10, fontWeight: 700, color: '#22c55e',
                                background: 'rgba(34,197,94,0.15)', padding: '1px 6px',
                                borderRadius: 3, textTransform: 'uppercase', letterSpacing: '0.3px',
                              }}>
                                ✓ {rnd}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-muted)' }}>{entry.team || '—'}</td>
                      {isTimeBased ? (
                        <td style={{ fontWeight: 700, color: 'var(--teal)', fontSize: 14, fontFamily: 'monospace' }}>{entry.bestTimeFormatted || '—'}</td>
                      ) : (
                        <td style={{ fontWeight: 700, color: 'var(--teal)', fontSize: 16 }}>{entry.totalPoints || 0}</td>
                      )}
                      <td>{entry.wins || 0}</td>
                      <td>{entry.podiums || 0}</td>
                      <td>{entry.races || 0}</td>
                      <td style={{ textAlign: 'center' }}>
                        {selectedEventId ? (
                          <button
                            className={`btn btn-sm ${advancedThisRound ? 'btn-primary' : 'btn-secondary'}`}
                            disabled={saving}
                            onClick={() => handleToggleAdvanced(entry.driverId, advancedThisRound)}
                            style={advancedThisRound ? { background: '#22c55e', borderColor: '#22c55e', minWidth: 100 } : { minWidth: 100 }}
                          >
                            {advancedThisRound ? '✓ Advanced' : 'Advance'}
                          </button>
                        ) : (
                          <span style={{ fontSize: 11, color: advancedAny ? '#22c55e' : 'var(--text-muted)' }}>
                            {advancedAny ? `✓ ${advancedRounds.length} round${advancedRounds.length !== 1 ? 's' : ''}` : '—'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {board?.updatedAt && (
            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10, marginBottom: 0 }}>
              Last updated: {board.updatedAt.toDate ? board.updatedAt.toDate().toLocaleString(undefined, { timeZone: 'America/Barbados' }) : '—'}
            </p>
          )}
        </div>
      )}

      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
